import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { OrganizationsService } from 'src/app/modules/organizations/services/organizations.service';
import { MdrDashboardsService } from '../../../services/mdr-dashboards.service';

import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexFill,
    ApexGrid,
    ApexLegend,
    ApexMarkers,
    ApexResponsive,
    ApexStroke,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import { MdrDashboardsSeriesDataInterface } from '../../../interfaces/mdr-dashboards-series-data.interface';
import { DashboardTable } from '../../../interfaces/mdr-dashboards-table-data.interface';
import { ChartAreaDataSource } from '../../../interfaces/mdr-dashboards-chart-area-data.interface';
import { Subscription } from 'rxjs/internal/Subscription';
import { TimeframeService } from '../../../../timeframe/services/timeframe.service';
import moment from 'moment';
import { distinctUntilChanged } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { TimeframeData } from '../../../../timeframe/interfaces/TimeframeData';
import { Subject } from 'rxjs';
import { ThemeService } from '../../../../../services/theme.service';

export type ChartOptionsStackedArea = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    dataLabels: ApexDataLabels;
    yaxis: ApexYAxis;
    colors: string[];
    legend: ApexLegend;
    fill: ApexFill;
    grid: ApexGrid;
    stroke: ApexStroke;
    tooltip: ApexTooltip;
    markers: ApexMarkers;
    responsive: ApexResponsive;
};

@Component({
    selector: 'app-mdr-dashboards-area-chart',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './mdr-dashboards-area-chart.component.html',
    styleUrls: ['./mdr-dashboards-area-chart.component.scss'],
    standalone: false,
})
export class MdrDashboardsAreaChartComponent implements OnInit, OnChanges {
    /**  startDate - start date of the chart */
    @Input() public startDate: string;

    /**  endDate - end date of the chart */
    @Input() public endDate: string;

    /**  colors - colors settings for each category of the series */
    @Input() public colors: string[];

    /* bulletType - set the bullet type for the legend. By default, is square */
    @Input() public bulletType = 'square';

    /**  strokeCurve - set the stroke curve type for the area chart. By default, is smooth */
    @Input() public strokeCurve = 'smooth';

    /**
     * element - element object passed from the tab's component.
     * This object will contain specific settings and custom options for the area chart widget
     */
    @Input() public element: ChartAreaDataSource;

    /**  strokeWidth - set the stroke width for the area chart. By default, is 1 */
    @Input() public strokeWidth = 1;

    /**  fillOpacity - set the fill opacity for series/category. By default, is 0.3 */
    @Input() public fillOpacity = 0.3;

    /**  fillType - set the fill type for series/category. By default, is solid */
    @Input() public fillType = 'solid';

    /**  tableLegend - enable/disable the table legend style for the specific widget */
    @Input() public tableLegend = true;

    /**  hasMarginTop - enable/disable margin top for the widget. If is true, the margin will be fixed and set inside HTML.*/
    @Input() public hasMarginTop = true;

    /**  width - widget width */
    @Input() public width: number | string = '100%';

    /**  height - widget height */
    @Input() public height: number | string = 220;

    /**
     * @var stacked - enable/disable stacked option for the area chart widget.
     * If it's true, then each category will be summed up with the previous one.
     */
    @Input() public stacked = false;

    /**  chartType - set chart type for the widget */
    @Input() public chartType = 'area';

    /**  showLegend - enable/disable legend */
    @Input() public showLegend = true;

    /**  tooltipWidth - set the width of the tooltip */
    @Input() public tooltipWidth = 220;

    /**  yaxisLogarithmicScale - enable/disable logarithmic scale for the Y axis */
    @Input() public yaxisLogarithmicScale = false;

    /**  legendDisplayedColumns - column names for the legend (if enabled) */
    @Input() public legendDisplayedColumns = ['name', 'value'];
    @Input() public currentTab = '';
    @Input() public emptyStateTitle = '';
    @Input() public emptyStateSubtitle = '';
    @Input() public className = '';
    public chartOptionsStackedArea: Partial<ChartOptionsStackedArea> | any;
    public emptyChartOptionsStackedArea: Partial<ChartOptionsStackedArea> | any;
    public loadedAllDataSubject: Subject<boolean> = new Subject<boolean>();
    public loadedAllData: boolean;
    public timeframeSubscription: Subscription;

    public constructor(
        public organizationService: OrganizationsService,
        public mdrDashboardsService: MdrDashboardsService,
        public timeframeService: TimeframeService,
        private cdr: ChangeDetectorRef,
        public themeService: ThemeService,
    ) {}

    @HostListener('window:resize', ['$event'])
    sizeChange(event: Event) {
        /* 'quick' fix for the problem where all the charts went missing after browser resizing */
        // @ts-ignore
        let chartRef = new ApexCharts(document.querySelector('#chartRef'), this.chartOptionsStackedArea);
        // @ts-ignore
        let emptyChartRef = new ApexCharts(document.querySelector('#chartRef'), this.emptyChartOptionsStackedArea);
    }

    public ngOnInit() {
        this.buildEmptyChart();
        this.timeframeSubscription = this.timeframeService
            .selectedTimeframe$()
            .pipe(distinctUntilChanged(isEqual))
            .subscribe((timeframe: TimeframeData) => {
                if (this.loadedAllData) {
                    this.buildNormalChart(timeframe);
                    this.buildEmptyChart();
                }
            });

        this.loadedAllDataSubject.asObservable().subscribe((val) => {
            this.loadedAllData = val;
            this.buildNormalChart(this.timeframeService.currentlySelectedTimeframe() as TimeframeData);
            this.buildEmptyChart();
        });
    }

    public buildNormalChart(timeframe: TimeframeData) {
        this.chartOptionsStackedArea = {
            series: [],
            grid: {
                show: true,
                borderColor: '#E2E2E2',
                strokeDashArray: 0,
                position: 'back',
                xaxis: {
                    lines: {
                        show: true,
                    },
                },
                yaxis: {
                    lines: {
                        show: true,
                    },
                },
                padding: {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 10,
                },
            },
            tooltip: {
                theme: this.themeService.isDarkMode ? 'dark' : 'light',
                shared: true,
                custom: ({
                    series,
                    seriesIndex,
                    dataPointIndex,
                    w,
                }: {
                    series: [];
                    seriesIndex: number;
                    dataPointIndex: number;
                    w: any;
                }) => {
                    /* get index for each category for hover action */
                    const hoverXaxis = w.globals.seriesX[seriesIndex][dataPointIndex];
                    const hoverIndexes = w.globals.seriesX.map((seriesX: any) => {
                        return seriesX.findIndex((xData: any) => xData === hoverXaxis);
                    });

                    let hoverList = '';

                    /* format date from datasource */
                    let formattedDate = new Date(hoverXaxis)
                        .toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                        })
                        .replace(/ /g, ' ');

                    /* if the selected timeframe is set to 24h, add hour and minute representation */
                    if (timeframe?.name == '24h') {
                        formattedDate = new Date(hoverXaxis)
                            .toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric',
                            })
                            .replace(/ /g, ' ');
                    }

                    /* calculate and render hover list for each point on the chart */
                    hoverIndexes.forEach((hoverIndex: number, seriesEachIndex: number) => {
                        if (hoverIndex >= 0) {
                            hoverList =
                                `
                                    <div class='gz-data-item'>
                                        <span class='${this.bulletType}-bullet-legend' style='background: ${
                                    this.element.strokeColors[seriesEachIndex]
                                }'></span><br />
                                        <div class='gz-data-item--title'>${w.globals.seriesNames[seriesEachIndex]}</div>
                                        <div class='gz-data-item--value'>${
                                            series[seriesEachIndex][hoverIndex] === 0.1 ? 0 : series[seriesEachIndex][hoverIndex]
                                        }</div>
                                    </div>
                            ` + hoverList;
                        }
                    });

                    return `<div class='chart-tooltip-container' style='min-width: ${this.tooltipWidth}px'>
                                <div class='chart-tooltip-title'>${formattedDate}</div>
                                ${hoverList}
                            </div>`;
                },
            },
            chart: {
                animations: {
                    enabled: false,
                },
                type: this.chartType,
                height: this.height,
                stacked: this.stacked,
                redrawOnParentResize: true,
                toolbar: {
                    show: false,
                },
                zoom: {
                    enabled: false,
                },
            },
            colors: this.colors,
            dataLabels: {
                enabled: false,
            },
            markers: {
                size: 0.0001 /* fix missing markers on Firefox */,
                colors: this.element.strokeColors,
            },
            fill: {
                type: this.fillType,
                opacity: this.fillOpacity,
            },
            legend: {
                show: false,
            },
            xaxis: {
                type: 'category',
                tickAmount: this.getTickCountBySelectedTimeframe(timeframe?.name),
                tooltip: {
                    enabled: false,
                },
                axisTicks: {
                    show: false,
                },
                labels: {
                    formatter: function (val: any, timestamp: any, element: any) {
                        if (timeframe?.name === '24h') {
                            return moment(timestamp).format('HH:mm');
                        }

                        if (timeframe?.name === '7d') {
                            return moment(timestamp).format('DD MMM');
                        }

                        if (timeframe?.name === '30d') {
                            return moment(timestamp).format('DD MMM');
                        }

                        return '';
                    }.bind(this),
                    hideOverlappingLabels: false,
                    style: {
                        colors: this.themeService.isDarkMode ? '#FFFFFF40' : '#00000080',
                        fontSize: '10px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 400,
                    },
                },
            },
            yaxis: {
                show: true,
                logarithmic: this.yaxisLogarithmicScale,
                forceNiceScale: true,
                max: function (val: number) {
                    return Math.floor(val) + 1;
                },
                logBase: 5,
                labels: {
                    style: {
                        colors: this.themeService.isDarkMode ? '#FFFFFF40' : '#00000080',
                        fontSize: '10px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 400,
                    },
                    formatter: function (val: number) {
                        if (val === Infinity) {
                            return 1;
                        }
                        return this.mdrDashboardsService.intToString(val ? Number(val.toFixed(0)) : 0, 1000, this.element.approximate);
                    }.bind(this),
                },
            },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        legend: {
                            position: 'bottom',
                            offsetX: -10,
                            offsetY: 0,
                        },
                    },
                },
            ],
            stroke: {
                width: this.strokeWidth,
                curve: this.strokeCurve,
                colors: this.element.strokeColors,
            },
        };
    }

    public buildEmptyChart() {
        this.emptyChartOptionsStackedArea = {
            series: [],
            grid: {
                show: true,
                borderColor: '#E2E2E2',
                strokeDashArray: 0,
                position: 'back',
                xaxis: {
                    lines: {
                        show: true,
                    },
                },
                yaxis: {
                    lines: {
                        show: true,
                    },
                },
                padding: {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 10,
                },
            },
            chart: {
                type: 'bar',
                width: this.width,
                height: this.height,
                toolbar: {
                    show: false,
                },
                zoom: {
                    enabled: false,
                },
            },
            colors: [],
            dataLabels: {
                enabled: false,
            },
            markers: {
                size: 0,
            },
            fill: {
                type: this.fillType,
                gradient: {
                    opacityFrom: 0.7,
                    opacityTo: 0.7,
                },
                opacity: this.fillOpacity,
            },
            legend: {
                show: false,
            },
            xaxis: {
                tickPlacement: 'between',
                labels: {
                    show: false,
                },
                axisTicks: {
                    show: false,
                },
            },
            yaxis: {
                show: false,
                tickAmount: 5,
                logarithmic: this.yaxisLogarithmicScale,
                labels: {
                    show: false,
                },
            },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        legend: {
                            position: 'bottom',
                            offsetX: -10,
                            offsetY: 0,
                        },
                    },
                },
            ],
            stroke: {
                width: this.strokeWidth,
                curve: this.strokeCurve,
            },
        };
    }

    public getTickCountBySelectedTimeframe(timeframe: string): number {
        if (timeframe === '24h') {
            return 6;
        }
        if (timeframe === '7d') {
            return 7;
        }
        if (timeframe === '30d') {
            return 10;
        }

        return 0;
    }

    /**
     * Function to map numeric values from 0 to 0.1 because we can't allow/calculate zero-values on a logarithmic scale.
     * If the yaxisLogarithmicScale input is disabled, return the given series without any processing.
     * @param series - data series to be processed
     */
    public mapSeriesToFitContent(series: ApexAxisChartSeries): ApexAxisChartSeries {
        if (this.yaxisLogarithmicScale) {
            series.forEach((item: any) => {
                for (const [index, value] of item.data.entries()) {
                    if (value[1] === 0) {
                        item.data[index].splice(1, 1, 0.1);
                    }
                }
            });
        }
        return series;
    }

    /**
     * Iterate through each category of the series and assign sum, name and color for every item.
     * For the sum calculation, we will replace every value of 0.1 with 0. We want the 0.1 value only for the
     * initial series (to avoid ApexCharts getting mad on logarithmic scale)
     * @param series - data series to be processed
     * @param colors - colors settings for each category
     */
    public calculateLegendTotal(series: MdrDashboardsSeriesDataInterface[], colors: string[]): DashboardTable[] {
        let data: DashboardTable[] = [];
        series.forEach((element: { name: string; data: [] }, index: number) => {
            let temp: DashboardTable = {};
            let sum = 0;

            element.data.forEach((value: number[]) => {
                sum += value[1] === 0.1 ? 0 : value[1];
            });

            temp.name = element.name;
            if (this.legendDisplayedColumns.includes('value')) {
                temp.value = this.mdrDashboardsService.intToString(sum, 1000, this.element.approximate);
            }
            temp.color = colors[index];
            data.unshift(<DashboardTable>temp);
        });

        return data;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes &&
            changes.currentTab &&
            changes.currentTab.currentValue &&
            changes.currentTab.currentValue !== changes.currentTab.previousValue
        ) {
            if (this.timeframeService.currentlySelectedTimeframe()) {
                const timeFrame = this.timeframeService.currentlySelectedTimeframe();
                this.buildNormalChart(timeFrame as TimeframeData);
            }
            this.buildEmptyChart();
            window.dispatchEvent(new Event('resize'));
            this.cdr.detectChanges();
        }
    }
}
